import React, { useState, useEffect } from "react";
import CryptoJS from "crypto-js";
import { ApiDetails, KEY } from "../../Constants";
import { DataGrid } from "@mui/x-data-grid";
import { styled, useTheme } from "@mui/material/styles";
import {
    Box,
    Typography,
    Paper,
    Select,
    MenuItem,
    FormControl,
    CircularProgress,
    InputLabel,
    Card,
    CardHeader,
    CardContent,
    IconButton,
    Chip,
    Tooltip,
    InputAdornment,
    TextField,
    Divider,
    Button,
    ButtonGroup,
    alpha
} from "@mui/material";
import { format } from "date-fns";
import { showSnackbar } from "../SnackBar/SnackBar";
import { handle401Unauthorized, handle403Forbidden } from "../../utils/authErrorHandler";
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EventIcon from '@mui/icons-material/Event';
import DateRangeIcon from '@mui/icons-material/DateRange';

const StyledCard = styled(Card)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius * 1.5,
    boxShadow: '0 4px 20px 0 rgba(0,0,0,0.05)',
    overflow: "hidden",
}));

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
    border: 'none',
    fontFamily: theme.typography.fontFamily,
    "& .MuiDataGrid-columnHeaders": {
        fontSize: "0.9rem",
        fontWeight: 600,
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        color: theme.palette.primary.dark,
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderRadius: 0,
    },
    "& .MuiDataGrid-row": {
        transition: 'background-color 0.2s ease',
    },
    "& .MuiDataGrid-row:hover": {
        backgroundColor: alpha(theme.palette.primary.light, 0.05),
    },
    "& .MuiDataGrid-row:nth-of-type(even)": {
        backgroundColor: alpha(theme.palette.background.default, 0.4),
    },
    "& .MuiDataGrid-cell:focus": {
        outline: "none",
    },
    "& .MuiDataGrid-cell": {
        fontSize: "0.875rem",
        padding: '12px 16px',
        color: theme.palette.text.primary,
    },
    "& .MuiDataGrid-footerContainer": {
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.03),
    },
    "& .MuiTablePagination-root": {
        color: theme.palette.text.secondary,
    },
}));

const filterOptions = [
    { value: "daily", label: "Today", icon: <CalendarTodayIcon fontSize="small" /> },
    { value: "weekly", label: "This Week", icon: <EventIcon fontSize="small" /> },
    { value: "monthly", label: "This Month", icon: <DateRangeIcon fontSize="small" /> },
];

export default function EnhancedTransactions() {
    const theme = useTheme();
    const [filter, setFilter] = useState("daily");
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [filteredTransactions, setFilteredTransactions] = useState([]);
    const [pageSize, setPageSize] = useState(10);

    const fetchTransactions = () => {
        setLoading(true);
        const encryptedData = localStorage.getItem("myMap");

        if (!encryptedData) {
            setLoading(false);
            console.warn("User data not found in localStorage");
            return;
        }

        try {
            let decrypted = CryptoJS.AES.decrypt(encryptedData, KEY);
            let originalText = decrypted.toString(CryptoJS.enc.Utf8);

            if (!originalText) {
                throw new Error("Failed to decrypt user data");
            }

            let parsedData = JSON.parse(originalText);

            // Safely extract required data with validation
            const landlordId = parsedData?.[0]?.[1];
            const token = parsedData?.[28]?.[1];

            if (!landlordId || !token) {
                throw new Error("Invalid user data structure - missing required fields");
            }

            fetch(ApiDetails + "pegasus/visionary/dashboard/get/home/transactions", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": token
                },
                body: JSON.stringify({
                    filter: filter,
                    landlord_id: landlordId,
                }),
            })
                .then(async (response) => {
                    // Handle authentication/authorization errors
                    if (response.status === 401) {
                        handle401Unauthorized("Your session has expired. Please log in again.");
                        return;
                    }
                    if (response.status === 403) {
                        handle403Forbidden("You don't have permission to view transactions.");
                        return;
                    }

                    let transactions = await response.json();
                    if (response.status === 200) {
                        setTransactions(transactions.transactions || []);
                        setFilteredTransactions(transactions.transactions || []);
                        showSnackbar({
                            title: "Success!",
                            message: `${filter.charAt(0).toUpperCase() + filter.slice(1)} transactions retrieved successfully.`,
                            type: "success",
                            position: { vertical: "bottom", horizontal: "right" },
                        });
                    } else {
                        showSnackbar({
                            title: "Error!",
                            message: `Failed fetching ${filter} transactions: ${transactions.Error || 'Unknown error'}`,
                            type: "error",
                            position: { vertical: "bottom", horizontal: "right" },
                        });
                    }
                })
                .catch((error) => {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed to fetch transactions. Please try again or contact support.",
                        type: "error",
                        position: { vertical: "bottom", horizontal: "right" },
                    });
                    console.error("Fetch error:", error);
                })
                .finally(() => setLoading(false));
        } catch (error) {
            setLoading(false);
            console.error("Decryption or parsing error:", error);
            // Don't show snackbar for every error - component will show "no data" state
        }
    };

    useEffect(() => {
        fetchTransactions();
    }, [filter]);

    useEffect(() => {
        if (!searchText) {
            setFilteredTransactions(transactions);
        } else {
            const lowercaseSearchText = searchText.toLowerCase();
            const filtered = transactions.filter((transaction) => {
                return (
                    (transaction.transaction_id && transaction.transaction_id.toString().toLowerCase().includes(lowercaseSearchText)) ||
                    (transaction.Name && transaction.Name.toLowerCase().includes(lowercaseSearchText)) ||
                    (transaction.amount && transaction.amount.toString().includes(lowercaseSearchText))
                );
            });
            setFilteredTransactions(filtered);
        }
    }, [searchText, transactions]);

    const handleFilterChange = (event) => {
        setFilter(event.target.value);
    };

    const handleSearchChange = (event) => {
        setSearchText(event.target.value);
    };

    const exportToCSV = () => {
        if (filteredTransactions.length === 0) {
            showSnackbar({
                title: "Warning",
                message: "No transactions available to export",
                type: "warning",
                position: { vertical: "bottom", horizontal: "right" },
            });
            return;
        }

        const csvHeader = ["Transaction ID", "Amount", "Name", "Date"];
        const csvRows = filteredTransactions.map((transaction) => [
            transaction.transaction_id,
            transaction.amount,
            transaction.Name,
            format(new Date(transaction.date_created), "dd MMM yyyy, HH:mm")
        ]);

        const csvContent = [
            csvHeader.join(","),
            ...csvRows.map(row => row.join(","))
        ].join("\n");

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${filter}_transactions_${format(new Date(), 'yyyy-MM-dd')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        showSnackbar({
            title: "Success!",
            message: "Transactions exported successfully",
            type: "success",
            position: { vertical: "bottom", horizontal: "right" },
        });
    };

    const columns = [
        {
            field: "transaction_id",
            headerName: "Transaction ID",
            minWidth: 150,
            flex: 1,
            headerAlign: "left",
            align: "left",
            renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ReceiptIcon fontSize="small" sx={{ mr: 1, color: theme.palette.primary.main, opacity: 0.7 }} />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {params.value}
                    </Typography>
                </Box>
            ),
        },
        {
            field: "amount",
            headerName: "Amount",
            minWidth: 150,
            flex: 1,
            headerAlign: "right",
            align: "right",
            renderCell: (params) => (
                <Typography
                    variant="body2"
                    sx={{
                        fontWeight: 600,
                        color: theme.palette.success.main,
                        bgcolor: alpha(theme.palette.success.light, 0.1),
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1
                    }}
                >
                    {params.row.amount.toLocaleString("en-KE", {
                        style: "currency",
                        currency: "KES",
                    })}
                </Typography>
            ),
        },
        {
            field: "Name",
            headerName: "Tenant",
            minWidth: 180,
            flex: 1.5,
            headerAlign: "left",
            align: "left",
            renderCell: (params) => (
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: "date_created",
            headerName: "Transaction Date",
            minWidth: 180,
            flex: 1.2,
            headerAlign: "center",
            align: "center",
            renderCell: (params) => (
                <Chip
                    icon={<CalendarTodayIcon fontSize="small" />}
                    label={format(new Date(params.value), "dd MMM yyyy, HH:mm")}
                    variant="outlined"
                    size="small"
                    sx={{
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                        color: theme.palette.text.secondary
                    }}
                />
            )
        },
    ];

    return (
        <StyledCard>
            <CardHeader
                title={
                    <Typography variant="h6" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                        Transaction History
                    </Typography>
                }
                subheader={
                    <Typography variant="body2" color="text.secondary">
                        View and manage your {filter} transaction records
                    </Typography>
                }
                action={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ButtonGroup variant="outlined" size="small">
                            <Tooltip title="Export Transactions">
                                <Button
                                    startIcon={<DownloadIcon />}
                                    onClick={exportToCSV}
                                    sx={{ borderRadius: '4px 0 0 4px' }}
                                >
                                    Export
                                </Button>
                            </Tooltip>
                            <Tooltip title="Refresh Transactions">
                                <Button
                                    onClick={fetchTransactions}
                                    disabled={loading}
                                    sx={{ borderRadius: '0 4px 4px 0' }}
                                >
                                    <RefreshIcon />
                                </Button>
                            </Tooltip>
                        </ButtonGroup>
                    </Box>
                }
            />

            <Divider />

            <CardContent sx={{ p: 0 }}>
                <Box sx={{ display: "flex", alignItems: "center", p: 2, gap: 2 }}>
                    <FormControl size="small" variant="outlined" sx={{ minWidth: 180 }}>
                        <InputLabel id="period-select-label">Time Period</InputLabel>
                        <Select
                            labelId="period-select-label"
                            id="period-select"
                            value={filter}
                            onChange={handleFilterChange}
                            label="Time Period"
                            startAdornment={
                                <InputAdornment position="start">
                                    <FilterListIcon fontSize="small" />
                                </InputAdornment>
                            }
                        >
                            {filterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        {option.icon}
                                        <Typography sx={{ ml: 1 }}>{option.label}</Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        variant="outlined"
                        size="small"
                        placeholder="Search transactions..."
                        value={searchText}
                        onChange={handleSearchChange}
                        sx={{ flexGrow: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" color="action" />
                                </InputAdornment>
                            ),
                        }}
                    />

                    {searchText && (
                        <Chip
                            label={`${filteredTransactions.length} results found`}
                            size="small"
                            color="primary"
                            variant="outlined"
                        />
                    )}
                </Box>

                {loading ? (
                    <Box sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: 300,
                        flexDirection: 'column',
                        gap: 2
                    }}>
                        <CircularProgress size={40} thickness={4} />
                        <Typography variant="body2" color="text.secondary">
                            Loading transactions...
                        </Typography>
                    </Box>
                ) : filteredTransactions.length === 0 ? (
                    <Box sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: 300,
                        flexDirection: 'column',
                        gap: 2
                    }}>
                        <ReceiptIcon sx={{ fontSize: 48, color: alpha(theme.palette.text.secondary, 0.3) }} />
                        <Typography variant="body2" color="text.secondary">
                            No transactions found for the selected period
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={fetchTransactions}
                        >
                            Refresh
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{
                        minHeight: 200,
                        maxHeight: 600,
                        height: `${Math.min(filteredTransactions.length * 52 + 56 + 52 + 20, 600)}px`,
                        width: '100%'
                    }}>
                        <StyledDataGrid
                            rows={filteredTransactions.map((row, index) => ({ ...row, id: index }))}
                            columns={columns}
                            disableSelectionOnClick
                            pageSize={pageSize}
                            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                            rowsPerPageOptions={[5, 10, 20, 50]}
                            pagination
                            disableColumnMenu
                            loading={loading}
                            sx={{ '& .MuiDataGrid-main': { px: 2 } }}
                        />
                    </Box>
                )}
            </CardContent>
        </StyledCard>
    );
}